import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import HistoryIcon from "@mui/icons-material/History";
import QuizIcon from "@mui/icons-material/Quiz";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FunctionsIcon from "@mui/icons-material/Functions";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ---- Types ----
type ConceptBuilderHelpProps = {
  open?: boolean;
  onClose?: () => void;
  /** If true (default), the component renders its own trigger IconButton and manages open state internally. */
  standalone?: boolean;
};

// ---- Shared UI atoms (styled like TemplateHelp) ----

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({
  title,
  subtitle,
  id,
}) => (
  <Box id={id} sx={{ mb: { xs: 1, sm: 1.25 }, mt: { xs: 2, sm: 3 } }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: 14, sm: 16 } }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 12 } }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const Bullet: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <ListItem sx={{ py: 0.3, alignItems: "flex-start", px: { xs: 0, sm: 0 } }}>
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        bgcolor: color,
        mt: "9px",
        mr: 1.25,
        flexShrink: 0,
      }}
    />
    <ListItemText
      primaryTypographyProps={{ 
        variant: "body2", 
        sx: { 
          color: "text.secondary", 
          lineHeight: 1.6,
          fontSize: { xs: 12, sm: 14 },
        } 
      }}
      primary={children}
    />
  </ListItem>
);

const ModeChip: React.FC<{ icon: React.ReactNode; label: string; color: string }> = ({
  icon,
  label,
  color,
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: { xs: 0.75, sm: 1 },
      py: 0.4,
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: `${color}33`,
      bgcolor: `${color}0d`,
      mr: 0.75,
      mb: 0.75,
    }}
  >
    <Box sx={{ color, display: "flex", alignItems: "center", "& svg": { fontSize: { xs: 11, sm: 13 } } }}>{icon}</Box>
    <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 600, color }}>{label}</Typography>
  </Box>
);

const SectionCard: React.FC<{
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
}> = ({ id, title, icon, color, children }) => (
  <Card variant="outlined" id={id} sx={{ borderRadius: { xs: 2, sm: 3 }, mb: 2 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 0.75, sm: 1 },
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: `${color}14`,
      }}
    >
      <Box sx={{ color, display: "grid", placeItems: "center", "& svg": { fontSize: { xs: 18, sm: 24 } } }}>
        {icon}
      </Box>
      <Typography fontWeight={700} sx={{ fontSize: { xs: 13, sm: 16 } }}>
        {title}
      </Typography>
    </Box>
    <CardContent sx={{ pt: 1.25, px: { xs: 1.5, sm: 2 } }}>
      <List dense sx={{ pt: 0 }}>
        {children}
      </List>
    </CardContent>
  </Card>
);

const ConceptBuilderHelp: React.FC<ConceptBuilderHelpProps> = ({
  open,
  onClose,
  standalone = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  const isOpen = standalone ? internalOpen : !!open;
  const handleClose = () => (standalone ? setInternalOpen(false) : onClose && onClose());

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
    // Close drawer on mobile after jump
    if (isMobile) {
      handleClose();
    }
  };

  // Full searchable text per section
  const sectionTexts = useMemo(
    () => ({
      context:
        "select your learning context subject sidebar physics mathematics topic subtopic narrow focus AI tailors every response to your exact class level and topic",
      ask:
        "ask questions type question press enter send button microphone dictate voice attach photos textbook pages handwritten problems paperclip icon predefined question cards jump-start topic instantly instruction mode default step-by-step solution step-by-step reasoning how the AI responds",
      diagrams:
        "ai-generated diagrams and images auto-generates visual diagrams flowcharts mind maps compact preview cards click to expand full size download button save as PNG generated images popup",
      practice:
        "practice and test yourself practice test generate a test on that topic difficulty easy medium hard you should also know concept questions new query fresh conversation same subject",
      math:
        "scientific keyboard and math input sigma button symbol keyboard greek letters operators fractions latex katex math rendering automatically",
      history:
        "chat history conversations saved automatically sidebar topic-wise all chats tabs switch reload including diagrams and images notification banner view jump to it another chat response",
      tips:
        "pro tips ask specific questions more context better answer reasoning mode understand why not just the answer attach photo tricky problem instant image-based help concept check questions deepen understanding",
    }),
    []
  );

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  // Responsive width
  const drawerWidth = isMobile ? '100%' : isTablet ? 480 : 560;

  return (
    <div>
      {standalone && (
        <Tooltip title="Help">
          <IconButton 
            onClick={() => setInternalOpen(true)}
            sx={{
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <HelpOutlineIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Tooltip>
      )}

      <Drawer 
        anchor="right" 
        open={isOpen} 
        onClose={handleClose} 
        sx={{ 
          zIndex: 1400,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            maxWidth: '100%',
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(102,126,234,0.16), rgba(16,185,129,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MenuBookIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: 14, sm: 20 } }}>
                Concept Builder • Help
              </Typography>
            </Stack>

            <IconButton onClick={handleClose} size={isMobile ? "small" : "medium"}>
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 } }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search context, diagrams, practice, math, history…"
              InputProps={{ 
                startAdornment: <SearchIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />,
                sx: { fontSize: { xs: 13, sm: 14 } }
              }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack 
              direction="row" 
              spacing={0.5} 
              sx={{ 
                mt: 1, 
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 0.75 },
              }}
            >
              {[
                { label: "Context", hash: "#sec-context" },
                { label: "Ask", hash: "#sec-ask" },
                { label: "Diagrams", hash: "#sec-diagrams" },
                { label: "Practice", hash: "#sec-practice" },
                { label: "Math", hash: "#sec-math" },
                { label: "History", hash: "#sec-history" },
                { label: "Tips", hash: "#sec-tips" },
              ].map((item) => (
                <Chip
                  key={item.label}
                  label={item.label}
                  size="small"
                  variant="outlined"
                  onClick={() => jumpTo(item.hash)}
                  sx={{ 
                    mb: 0.5,
                    fontSize: { xs: 9, sm: 11 },
                    height: { xs: 24, sm: 32 },
                    '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ my: { xs: 1, sm: 2 } }} />

          {/* Scrollable Content */}
          <Box 
            ref={contentRef} 
            sx={{ 
              px: { xs: 1.5, sm: 2 }, 
              pb: 3, 
              overflowY: "auto",
              flex: 1,
            }}
          >
            {/* Overview */}
            <SectionHeader
              title="How to Use Concept Builder"
              subtitle="A quick walkthrough of every feature, from picking a topic to testing yourself."
            />
            <Alert 
              severity="info" 
              icon={<InfoOutlinedIcon />} 
              sx={{ 
                mb: 1,
                fontSize: { xs: 12, sm: 14 },
                '& .MuiAlert-icon': { fontSize: { xs: 18, sm: 22 } },
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              <Typography sx={{ fontSize: { xs: 12, sm: 14 } }}>
                The AI tailors every response to the subject, topic and subtopic you select — so pick your
                learning context first for the most accurate answers.
              </Typography>
            </Alert>

            {/* Sections - only show matching ones */}
            {matches("context") && (
              <SectionCard
                id="sec-context"
                title="1 · Select Your Learning Context"
                icon={<AccountTreeIcon />}
                color="#667eea"
              >
                <Bullet color="#667eea">
                  Choose a <strong>Subject</strong> from the sidebar (e.g. Physics, Mathematics).
                </Bullet>
                <Bullet color="#667eea">
                  Pick a <strong>Topic</strong> and <strong>Subtopic</strong> to narrow your focus.
                </Bullet>
                <Bullet color="#667eea">
                  The AI tailors every response to your exact class level and topic.
                </Bullet>
              </SectionCard>
            )}

            {matches("ask") && (
              <SectionCard
                id="sec-ask"
                title="2 · Ask Questions"
                icon={<AutoAwesomeIcon />}
                color="#10b981"
              >
                <Bullet color="#10b981">
                  Type any question and press <strong>Enter</strong> or the Send button.
                </Bullet>
                <Bullet color="#10b981">
                  Tap the <strong>🎤 microphone</strong> to dictate your question by voice.
                </Bullet>
                <Bullet color="#10b981">
                  Attach photos of textbook pages or handwritten problems using the <strong>📎 icon</strong>.
                </Bullet>
                <Bullet color="#10b981">
                  Click predefined question cards to jump-start a topic instantly.
                </Bullet>
                <Box sx={{ mt: 1, pl: 0.25 }}>
                  <Typography sx={{ fontSize: { xs: 10.5, sm: 11.5 }, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
                    Instruction Mode — choose how the AI responds:
                  </Typography>
                  <ModeChip icon={<InfoOutlinedIcon />} label="Default" color="#667eea" />
                  <ModeChip icon={<SchoolIcon />} label="Step-by-Step Solution" color="#f59e0b" />
                  <ModeChip icon={<LightbulbIcon />} label="Step-by-Step Reasoning" color="#8b5cf6" />
                </Box>
              </SectionCard>
            )}

            {matches("diagrams") && (
              <SectionCard
                id="sec-diagrams"
                title="3 · AI-Generated Diagrams & Images"
                icon={<AutoAwesomeIcon />}
                color="#f59e0b"
              >
                <Bullet color="#f59e0b">
                  The AI auto-generates <strong>visual diagrams</strong> (flowcharts, mind maps) when helpful.
                </Bullet>
                <Bullet color="#f59e0b">
                  Diagrams appear as compact preview cards — <strong>click to expand</strong> full size.
                </Bullet>
                <Bullet color="#f59e0b">
                  Use the <strong>⬇ Download</strong> button on any diagram or image to save as PNG.
                </Bullet>
                <Bullet color="#f59e0b">
                  Generated images also open in a popup with full download support.
                </Bullet>
              </SectionCard>
            )}

            {matches("practice") && (
              <SectionCard
                id="sec-practice"
                title="4 · Practice & Test Yourself"
                icon={<QuizIcon />}
                color="#ef4444"
              >
                <Bullet color="#ef4444">
                  Click <strong>"Practice Test"</strong> after any answer to generate a test on that topic.
                </Bullet>
                <Bullet color="#ef4444">
                  Choose difficulty — <strong>Easy</strong>, <strong>Medium</strong>, or <strong>Hard</strong>.
                </Bullet>
                <Bullet color="#ef4444">
                  Explore <strong>"You Should Also Know"</strong> concept questions below each answer.
                </Bullet>
                <Bullet color="#ef4444">
                  Click <strong>"New Query"</strong> to start a fresh conversation on the same subject.
                </Bullet>
              </SectionCard>
            )}

            {matches("math") && (
              <SectionCard
                id="sec-math"
                title="5 · Scientific Keyboard & Math Input"
                icon={<FunctionsIcon />}
                color="#8b5cf6"
              >
                <Bullet color="#8b5cf6">
                  Tap the <strong>Σ</strong> button to open the scientific symbol keyboard.
                </Bullet>
                <Bullet color="#8b5cf6">
                  Insert Greek letters, operators, fractions and more with one click.
                </Bullet>
                <Bullet color="#8b5cf6">
                  AI responses support full <strong>LaTeX / KaTeX</strong> math rendering automatically.
                </Bullet>
              </SectionCard>
            )}

            {matches("history") && (
              <SectionCard
                id="sec-history"
                title="6 · Chat History"
                icon={<HistoryIcon />}
                color="#0ea5e9"
              >
                <Bullet color="#0ea5e9">All conversations are saved automatically in the sidebar.</Bullet>
                <Bullet color="#0ea5e9">
                  Switch between <strong>Topic-wise</strong> and <strong>All Chats</strong> tabs in the sidebar.
                </Bullet>
                <Bullet color="#0ea5e9">
                  Click any past conversation to reload it, including all diagrams and images.
                </Bullet>
                <Bullet color="#0ea5e9">
                  If a response arrives in another chat, a notification banner will appear — click{" "}
                  <strong>"View"</strong> to jump to it.
                </Bullet>
              </SectionCard>
            )}

            {/* Pro Tips */}
            {matches("tips") && (
              <Card
                id="sec-tips"
                variant="outlined"
                sx={{ 
                  borderRadius: { xs: 2, sm: 3 }, 
                  border: "1px solid #fde68a", 
                  bgcolor: "#fffbeb", 
                  mb: 1 
                }}
              >
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1.2, 
                  px: { xs: 1.5, sm: 2 }, 
                  py: { xs: 1, sm: 1.25 } 
                }}>
                  <TipsAndUpdatesIcon sx={{ color: "#d97706", fontSize: { xs: 18, sm: 24 } }} />
                  <Typography fontWeight={700} sx={{ color: "#92400e", fontSize: { xs: 14, sm: 16 } }}>
                    Pro Tips
                  </Typography>
                </Box>
                <CardContent sx={{ pt: 0, px: { xs: 1.5, sm: 2 } }}>
                  <List dense sx={{ pt: 0 }}>
                    <Bullet color="#d97706">Ask specific questions — the more context you give, the better the answer.</Bullet>
                    <Bullet color="#d97706">
                      Switch to "Reasoning" mode when you want to understand the <em>why</em>, not just the answer.
                    </Bullet>
                    <Bullet color="#d97706">Attach a photo of a tricky problem for instant image-based help.</Bullet>
                    <Bullet color="#d97706">Use concept check questions after each answer to deepen your understanding.</Bullet>
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Footer CTA */}
            <Box sx={{ mt: 2 }}>
              <Button
                onClick={handleClose}
                variant="contained"
                fullWidth
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  bgcolor: "#667eea",
                  boxShadow: "none",
                  fontSize: { xs: 13, sm: 16 },
                  py: { xs: 1, sm: 1.5 },
                  "&:hover": { bgcolor: "#5568d3", boxShadow: "none" },
                }}
              >
                Got it, let's learn! 🚀
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default ConceptBuilderHelp;